/*
 * Copyright © HatioLab Inc. All rights reserved.
 *
 * Stocker Location Rule Editor
 */

import { css, html, TemplateResult } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { OxPropertyEditor, PropertySpec } from '@operato/property-editor'
import type { LocationRule } from '../stocker.js'
import { DEFAULT_LOCATION_RULE, buildLocationId } from '../stocker.js'

@customElement('property-editor-stocker-location')
export default class StockerLocationEditor extends OxPropertyEditor {
  static styles = [
    ...OxPropertyEditor.styles,
    css`
      .loc-grid { display: grid; grid-template-columns: 80px 1fr; gap: 3px 6px; font-size: 11px; align-items: center; }
      .loc-grid label { color: #555; font-weight: 500; font-size: 10px; }
      .loc-grid input, .loc-grid select {
        width: 100%; box-sizing: border-box; font-size: 11px; padding: 3px 4px;
        border: 1px solid rgba(0,0,0,0.15); border-radius: 4px; background: #fff; color: #1c1b1f;
      }
      .loc-grid input[type="number"] { width: 50px; }
      .side-labels { display: flex; gap: 4px; align-items: center; font-size: 10px; }
      .side-labels input { width: 30px; text-align: center; }
      .section-title { grid-column: 1 / -1; font-weight: 600; font-size: 10px; color: #6750a4; margin-top: 6px; padding-bottom: 2px; border-bottom: 1px solid rgba(0,0,0,0.08); }
      .preview { grid-column: 1 / -1; margin-top: 6px; padding: 6px 8px; background: #f3edf7; border-radius: 6px; font-family: monospace; font-size: 11px; }
      .preview-title { font-size: 9px; font-weight: 600; color: #555; margin-bottom: 3px; }
      .preview-samples { display: flex; flex-wrap: wrap; gap: 4px; }
      .preview-sample { padding: 1px 5px; background: #fff; border-radius: 3px; font-size: 10px; }
    `
  ]

  @state() private declare _rule: LocationRule
  private _lastExternalValue: any = undefined

  constructor() {
    super()
    this._rule = { ...DEFAULT_LOCATION_RULE }
  }

  private _parseValue(value: any): LocationRule {
    if (!value) return { ...DEFAULT_LOCATION_RULE }
    if (typeof value === 'string') {
      try { return { ...DEFAULT_LOCATION_RULE, ...JSON.parse(value) } } catch { return { ...DEFAULT_LOCATION_RULE } }
    }
    return { ...DEFAULT_LOCATION_RULE, ...value }
  }

  editorTemplate(value: any, _spec: PropertySpec): TemplateResult {
    const serialized = typeof value === 'string' ? value : JSON.stringify(value)
    if (serialized !== this._lastExternalValue) {
      this._lastExternalValue = serialized
      this._rule = this._parseValue(value)
    }

    const r = this._rule
    const rackConfig = { bays: 10, levels: 5, depthCount: 1 }
    const samples = [
      buildLocationId(r, 'L', 1, 1, 1, rackConfig),
      buildLocationId(r, 'L', 5, 3, 1, rackConfig),
      buildLocationId(r, 'R', 10, 5, 1, rackConfig)
    ]

    return html`
      <fieldset fullwidth>
        <div class="loc-grid">
          <div class="section-title">Pattern</div>
          <label>Format</label>
          <input type="text" .value=${r.pattern} placeholder="{side}{bay}-{level}-{depth}"
            @change=${(e: Event) => { e.stopPropagation(); this._set('pattern', (e.target as HTMLInputElement).value) }} />

          <label>Side labels</label>
          <div class="side-labels">
            L=<input type="text" .value=${r.sideLabels.L}
              @change=${(e: Event) => { e.stopPropagation(); this._setSideLabel('L', (e.target as HTMLInputElement).value) }} />
            R=<input type="text" .value=${r.sideLabels.R}
              @change=${(e: Event) => { e.stopPropagation(); this._setSideLabel('R', (e.target as HTMLInputElement).value) }} />
          </div>

          <div class="section-title">Direction</div>
          <label>Bay</label>
          <select .value=${r.bayDir}
            @change=${(e: Event) => { e.stopPropagation(); this._set('bayDir', (e.target as HTMLSelectElement).value) }}>
            <option value="ltr">Left → Right</option>
            <option value="rtl">Right → Left</option>
          </select>

          <label>Level</label>
          <select .value=${r.levelDir}
            @change=${(e: Event) => { e.stopPropagation(); this._set('levelDir', (e.target as HTMLSelectElement).value) }}>
            <option value="btt">Bottom → Top</option>
            <option value="ttb">Top → Bottom</option>
          </select>

          <div class="section-title">Start number</div>
          <label>Bay start</label>
          <input type="number" .value=${String(r.bayStart)} min="0" step="1"
            @change=${(e: Event) => { e.stopPropagation(); this._set('bayStart', Number((e.target as HTMLInputElement).value)) }} />
          <label>Level start</label>
          <input type="number" .value=${String(r.levelStart)} min="0" step="1"
            @change=${(e: Event) => { e.stopPropagation(); this._set('levelStart', Number((e.target as HTMLInputElement).value)) }} />
          <label>Depth start</label>
          <input type="number" .value=${String(r.depthStart)} min="0" step="1"
            @change=${(e: Event) => { e.stopPropagation(); this._set('depthStart', Number((e.target as HTMLInputElement).value)) }} />

          <div class="section-title">Digit padding</div>
          <label>Bay digits</label>
          <input type="number" .value=${String(r.bayDigits)} min="0" max="4" step="1"
            @change=${(e: Event) => { e.stopPropagation(); this._set('bayDigits', Number((e.target as HTMLInputElement).value)) }} />
          <label>Level digits</label>
          <input type="number" .value=${String(r.levelDigits)} min="0" max="4" step="1"
            @change=${(e: Event) => { e.stopPropagation(); this._set('levelDigits', Number((e.target as HTMLInputElement).value)) }} />
          <label>Depth digits</label>
          <input type="number" .value=${String(r.depthDigits)} min="0" max="4" step="1"
            @change=${(e: Event) => { e.stopPropagation(); this._set('depthDigits', Number((e.target as HTMLInputElement).value)) }} />

          <div class="preview">
            <div class="preview-title">Preview (10 bays, 5 levels)</div>
            <div class="preview-samples">
              ${samples.map(s => html`<span class="preview-sample">${s}</span>`)}
            </div>
          </div>
        </div>
      </fieldset>
    `
  }

  private _set(field: string, value: any) {
    this._rule = { ...this._rule, [field]: value }
    this._apply()
  }

  private _setSideLabel(side: 'L' | 'R', value: string) {
    this._rule = { ...this._rule, sideLabels: { ...this._rule.sideLabels, [side]: value || side } }
    this._apply()
  }

  private _apply() {
    const jsonValue = JSON.stringify(this._rule)
    this.value = jsonValue
    this._lastExternalValue = jsonValue
    this.requestUpdate()

    this.dispatchEvent(new CustomEvent('i-need-selected', {
      bubbles: true, composed: true,
      detail: { callback: (selected: any[]) => { selected[0]?.set('locationRule', jsonValue) } }
    }))
  }
}
